import { useParams } from 'react-router'
import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { LISTINGS } from './RealAssetsScreen'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

const DOCUMENTS = [
  ['Valuation report', 'PDF · 2.4 MB · June 2026'],
  ['Title and ownership', 'PDF · 1.1 MB'],
  ['Rental agreement template', 'PDF · 640 KB'],
  ['Vehicle structure', 'PDF · 880 KB'],
] as const

export function AssetDetailScreen() {
  const { id } = useParams()
  const listing = LISTINGS.find((item) => item.id === id) ?? LISTINGS[0]!

  return (
    <>
      <PageHeader
        title={listing.name}
        subtitle={`${listing.city} · ${listing.kind} · ${listing.completes}`}
        actions={<Button variant="ghost" size="md" to="/real-assets">All buildings</Button>}
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Stack direction="row" gap={8} wrap>
              {[
                ['Target yield', listing.yieldRate],
                ['Minimum', listing.minimum],
                ['Funded', `${Math.round(listing.funded * 100)}%`],
              ].map(([label, value]) => (
                <Stack key={label} gap={1}>
                  <Text variant="eyebrow" tone="secondary">{label}</Text>
                  <Text variant="title" weight="bold" numeric>{value}</Text>
                </Stack>
              ))}
            </Stack>
            <div className="pb-mt-5">
              <Meter value={listing.funded} height={8} tone="ink" label="Funding progress" />
            </div>
            <Text variant="caption" tone="secondary" className="pb-mt-4">
              Rent is collected locally and paid to holders on the 1st of each month, after a 1.0%
              platform fee. If the building sits empty, there is no rent that month.
            </Text>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">Documents</Text>
            <Stack gap={0} className="pb-mt-3">
              {DOCUMENTS.map(([name, meta]) => (
                <button key={name} type="button" className={checkout.line} style={{ width: '100%' }}>
                  <Icon name="description" size={20} />
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">{name}</Text>
                    <Text variant="eyebrow" tone="secondary">{meta}</Text>
                  </Stack>
                  <Icon name="download" size={18} />
                </button>
              ))}
            </Stack>
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">Invest from</Text>
              <Pill tone={listing.status === 'building' ? 'warning' : 'accent'}>
                {listing.status === 'building' ? 'Building' : 'Renting'}
              </Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {listing.minimum}
            </Text>
            <div className={checkout.rule} />
            {[
              ['Target yield', listing.yieldRate],
              ['Platform fee', '1.0% of rent'],
              ['Rent paid', 'Monthly, on the 1st'],
              ['Sell before completion', 'Secondary market only'],
            ].map(([label, value]) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">{label}</Text>
                <Text variant="caption" weight="semibold" tone="onDark" numeric>{value}</Text>
              </div>
            ))}
            <Card.Footer>
              <Button size="lg" block iconEnd="arrow_forward" to="/real-assets/review">Invest in this building</Button>
              <Text variant="eyebrow" tone="onDarkMuted" className="pb-mt-3">
                Illiquid and not covered by deposit insurance. You can lose what you put in.
              </Text>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
